import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaRocket, FaBrain, FaUsers, FaCog } from 'react-icons/fa';
import { AnimationVariants } from '../types';
import { useLanguage } from '../i18n';

const HeroContainer = styled.section`
  min-height: 100vh;
  background: ${props => props.theme.gradients.hero};
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 80px; /* 为固定导航留出空间 */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><defs><radialGradient id="a" cx="50%" cy="50%"><stop offset="0%" stop-color="%23ffffff" stop-opacity="0.1"/><stop offset="100%" stop-color="%23ffffff" stop-opacity="0"/></radialGradient></defs><circle cx="200" cy="200" r="100" fill="url(%23a)"/><circle cx="800" cy="300" r="150" fill="url(%23a)"/><circle cx="400" cy="700" r="120" fill="url(%23a)"/><circle cx="900" cy="800" r="80" fill="url(%23a)"/></svg>');
    opacity: 0.3;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
`;

const LeftContent = styled.div`
  color: white;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 32px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: white;
  color: ${props => props.theme.colors.primary};
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadows.lg};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    color: ${props => props.theme.colors.primary};
  }
`;

const RightContent = styled.div`
  position: relative;
`;

const FloatingCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 32px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    border-radius: 20px;
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.9;
  margin-bottom: 24px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const StatItem = styled.div`
  text-align: center;
  
  .stat-number {
    font-size: 2rem;
    font-weight: 800;
    color: white;
    display: block;
  }
  
  .stat-label {
    font-size: 0.9rem;
    opacity: 0.8;
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const FloatingIcon = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

interface HeroProps {
  id?: string;
}

const Hero: React.FC<HeroProps> = ({ id }) => {
  const { t } = useLanguage();
  const containerVariants: AnimationVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants: AnimationVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingVariants: AnimationVariants = {
    float: {
      y: [-10, 10, -10],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <HeroContainer id={id}>
      <HeroContent>
        <LeftContent>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Title variants={itemVariants}>
              {t('hero.title')}
            </Title>
            <Subtitle variants={itemVariants}>
              {t('hero.subtitle')}
            </Subtitle>
            <CTAButtons variants={itemVariants}>
              <PrimaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('hero.cta.primary')}
              </PrimaryButton>
              <SecondaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('hero.cta.secondary')}
              </SecondaryButton>
            </CTAButtons>
          </motion.div>
        </LeftContent>
        
        <RightContent>
          <FloatingCard
            initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <CardContent>
              <CardTitle>
                <FaRocket />
                {t('hero.card.title')}
              </CardTitle>
              <CardDescription>
                {t('hero.card.desc')}
              </CardDescription>
              <StatsGrid>
                <StatItem>
                  <span className="stat-number">4+</span>
                  <span className="stat-label">{t('hero.stat.coreIndustries')}</span>
                </StatItem>
                <StatItem>
                  <span className="stat-number">100%</span>
                  <span className="stat-label">{t('hero.stat.customized')}</span>
                </StatItem>
                <StatItem>
                  <span className="stat-number">{t('hero.stat.fast')}</span>
                  <span className="stat-label">{t('hero.stat.dev')}</span>
                </StatItem>
                <StatItem>
                  <span className="stat-number">{t('hero.stat.ai')}</span>
                  <span className="stat-label">{t('hero.stat.landing')}</span>
                </StatItem>
              </StatsGrid>
            </CardContent>
          </FloatingCard>
          
          <FloatingElements>
            <FloatingIcon
              style={{ top: '10%', right: '10%' }}
              variants={floatingVariants}
              animate="float"
            >
              <FaBrain />
            </FloatingIcon>
            <FloatingIcon
              style={{ bottom: '20%', left: '5%' }}
              variants={floatingVariants}
              animate="float"
              transition={{ delay: 1 }}
            >
              <FaUsers />
            </FloatingIcon>
            <FloatingIcon
              style={{ top: '60%', right: '5%' }}
              variants={floatingVariants}
              animate="float"
              transition={{ delay: 2 }}
            >
              <FaCog />
            </FloatingIcon>
          </FloatingElements>
        </RightContent>
      </HeroContent>
    </HeroContainer>
  );
};

export default Hero;
